import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { InvoiceException, InvoiceRecord } from '@/types/domain/invoice-management';
import { AlertTriangle, CheckCircle2, RefreshCw, X, ShieldAlert } from 'lucide-react';

interface InvoiceExceptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  exception: InvoiceException | null;
  invoice: InvoiceRecord;
  onResolve: (exceptionId: string, notes: string) => Promise<void>;
}

export const InvoiceExceptionModal: React.FC<InvoiceExceptionModalProps> = ({
  isOpen,
  onClose,
  exception,
  invoice,
  onResolve,
}) => {
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen || !exception) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolutionNotes.trim()) {
      setErrorMessage('Please provide justification notes explaining the exception override.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await onResolve(exception.id, resolutionNotes);
      onClose();
    } catch (err: unknown) {
      setErrorMessage((err as Error).message || 'Failed to resolve exception.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-start justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-600">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-foreground">
                Resolve Invoice Discrepancy Exception
              </h3>
              <p className="text-xs text-foreground-secondary">
                {invoice.invoiceNumber} | {exception.code.replace(/_/g, ' ')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-foreground-muted hover:text-foreground p-1 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {errorMessage && (
          <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-800 text-xs font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="p-4 rounded-xl border border-border/70 bg-surface/50 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Exception Code:</span>
            <span className="font-mono font-bold text-rose-600">{exception.code}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Severity:</span>
            <span className="font-extrabold uppercase text-xs px-2 py-0.5 rounded bg-rose-500/10 text-rose-700">
              {exception.severity}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Expected Value:</span>
            <span className="font-mono font-bold text-emerald-600">{exception.expectedValue}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Billed Actual:</span>
            <span className="font-mono font-black text-rose-600">{exception.actualValue}</span>
          </div>
          <div className="pt-2 border-t border-border/40">
            <span className="text-foreground-secondary font-medium block mb-1">Discrepancy Details:</span>
            <p className="text-foreground font-medium bg-card p-2.5 rounded-lg border border-border/60">
              {exception.description}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-xs">
          <div className="space-y-1.5">
            <label className="font-bold text-foreground block">
              Formal AP Resolution Justification & Audit Notes *
            </label>
            <textarea
              required
              rows={3}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="e.g. Price variance of $50 authorized per signed amendment letter from Procurement Director. Approved for payment."
              className="w-full p-3 rounded-xl border border-border bg-surface text-foreground font-medium placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border/40">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Resolving...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Override & Clear Exception
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
